import React, { useState, useEffect } from 'react';
import { Trophy, Plus, AlertTriangle, Check, UserCog } from 'lucide-react';
import api from '@/lib/api';
import { AddDoctoreModal, SpecializeModal, AssignMentorModal, UpgradeSanctumModal } from '@/components/SanctumModals';
import { CLASS_IMAGE } from '@/lib/game';
import { btn, Empty, Loader } from '@/components/Page';

const DoctoreSanctumPage = () => {
  const [loading, setLoading] = useState(true);
  const [sanctumData, setSanctumData] = useState(null);
  const [activeGladiators, setActiveGladiators] = useState([]);
  const [showAddDoctore, setShowAddDoctore] = useState(false);
  const [showSpecialize, setShowSpecialize] = useState(false);
  const [showAssignMentor, setShowAssignMentor] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedGladiator, setSelectedGladiator] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sanctumRes, gladRes] = await Promise.all([
        api.get('/sanctum'),
        api.get('/gladiators')
      ]);

      setSanctumData(sanctumRes.data);
      setActiveGladiators(gladRes.data.active || []);
      setError('');
    } catch (err) {
      setError('Failed to load sanctum data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const removeDoctore = async (gladId) => {
    if (!window.confirm('Remove this doctore from your sanctum?')) return;

    try {
      await api.delete(`/sanctum/doctores/${gladId}`);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove doctore');
      console.error(err);
    }
  };

  const getUpgradeCost = () => {
    const slots = sanctumData?.sanctum_slots || 1;
    const costs = { 1: 200, 2: 300, 3: 400, 4: 500 };
    return costs[slots] || null;
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'text-amber-700',
      silver: 'text-gray-400',
      gold: 'text-yellow-500',
      elite: 'text-purple-500',
      legendary: 'text-orange-500'
    };
    return colors[tier] || 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 px-4 py-8 sm:px-8">
        <Loader label="Preparing Doctore's Sanctum…" />
      </div>
    );
  }

  return (
    <main data-testid="doctore-sanctum-page" className="min-h-screen overflow-x-clip bg-zinc-950 px-4 py-6 text-zinc-100 sm:px-8 sm:py-8">
      <div className="mx-auto max-w-7xl">
      <section className="relative mb-6 overflow-hidden border border-amber-900/40 rounded-sm">
        <img 
          src="/doctore-bg.png" 
          alt="Doctore's Sanctum"
          className="h-40 w-full object-cover object-center sm:h-52"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
          <div>
            <h1 className="font-cinzel text-2xl text-amber-300 uppercase tracking-wide sm:text-4xl">Doctore&apos;s Sanctum</h1>
            <p className="mt-1 text-sm text-zinc-300">Retired champions who forge the next generation.</p>
          </div>
        </div>
      </section>

      <div>
        {/* Error Display */}
        {error && (
          <div data-testid="sanctum-error" className="mb-6 border border-red-900/50 bg-red-950/20 p-4 rounded-sm flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-400 text-sm">{error}</div>
          </div>
        )}

        <div className="space-y-6">
          {/* Doctores Section */}
          <section className="border border-zinc-800 bg-zinc-900/60 p-4 rounded-sm sm:p-6">
            <div className="mb-5 flex flex-col gap-4 border-b border-zinc-800 pb-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-cinzel font-semibold text-amber-500 uppercase tracking-wide">
                  Your Doctores ({sanctumData?.doctores?.length || 0}/{sanctumData?.sanctum_slots || 1})
                </h2>
                {(sanctumData?.sanctum_slots || 1) < 5 && (
                  <p className="mt-1 text-sm text-zinc-500">
                    Upgrade for {getUpgradeCost()}g to add more slots
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(sanctumData?.sanctum_slots || 1) < 5 && (
                  <button
                    data-testid="sanctum-upgrade-button"
                    onClick={() => setShowUpgradeModal(true)}
                    className={btn('ghost') + " flex items-center gap-2 !border-emerald-900/60 !text-emerald-300 hover:!border-emerald-600"}
                  >
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm">Upgrade ({getUpgradeCost()}g)</span>
                  </button>
                )}
                {(sanctumData?.doctores?.length || 0) < (sanctumData?.sanctum_slots || 1) && (
                  <button
                    data-testid="sanctum-add-doctore-button"
                    onClick={() => setShowAddDoctore(true)}
                    className={btn() + " flex items-center gap-2"}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">Add Doctore</span>
                  </button>
                )}
              </div>
            </div>

            {sanctumData?.doctores?.length === 0 ? (
              <Empty label="No doctores yet"><p className="text-sm text-zinc-600">Retire gladiators with 50+ battles to convert them into master trainers.</p></Empty>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                {sanctumData.doctores.map((doc) => {
                  return (
                    <div
                      key={doc.glad_id}
                      className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm transition-colors hover:border-amber-700/50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-start gap-4">
                          <img 
                            src={CLASS_IMAGE[doc.class] || CLASS_IMAGE.Provocator} 
                            alt={doc.class}
                            className="h-12 w-12 rounded-sm border border-amber-900/50 object-cover"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-amber-100">{doc.name}</h4>
                              <span className="text-xs text-zinc-500">({doc.class})</span>
                            </div>
                            <div className={`text-sm font-semibold mb-1 ${getTierColor(doc.tier)}`}>
                              {doc.tier_name} (±{Math.round(doc.bonus_pct * 100)}%)
                            </div>
                            <div className="mb-2 text-xs text-zinc-500">
                              {doc.total_battles} career battles
                            </div>
                          </div>
                        </div>
                        <button
                          data-testid={`sanctum-remove-doctore-${doc.glad_id}`}
                          onClick={() => removeDoctore(doc.glad_id)}
                          className="p-2 text-zinc-500 transition-colors hover:bg-red-950/30 hover:text-red-400"
                          title="Remove doctore"
                        >
                          <AlertTriangle className="w-4 h-4 text-gray-500 hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Specialized Training Section */}
          <section className="border border-zinc-800 bg-zinc-900/60 p-4 rounded-sm sm:p-6">
            <h2 className="mb-5 border-b border-zinc-800 pb-4 text-lg font-cinzel font-semibold uppercase tracking-wide text-amber-300">Specialized Training</h2>
            
            {sanctumData?.doctores?.length === 0 ? (
              <Empty label="Add a doctore first to begin specializing your gladiators" />
            ) : (
              <div>
                <p className="text-sm text-zinc-400 mb-4">
                  Select an active gladiator to apply permanent specialized training (ONE TIME USE per gladiator)
                </p>
                
                <div className="grid gap-3">
                  {activeGladiators.map((glad) => {
                    const hasSpec = glad.specialization;
                    return (
                      <div
                        key={glad.id}
                        data-testid={`sanctum-specialize-${glad.id}`}
                        className={`p-4 border rounded-sm transition-colors ${
                          hasSpec
                            ? 'bg-zinc-950/40 border-zinc-800'
                            : 'bg-zinc-950/60 border-zinc-800 hover:border-amber-700/50 cursor-pointer'
                        }`}
                        onClick={() => !hasSpec && setSelectedGladiator(glad)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="font-semibold text-amber-100">{glad.name}</div>
                              <div className="text-xs text-zinc-500">
                                {glad.cls || glad.class} • Rating {glad.rating}
                              </div>
                            </div>
                          </div>
                          
                          {hasSpec ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500" />
                              <span className="text-zinc-400">
                                Specialized by {glad.specialization.doctore_name}
                              </span>
                            </div>
                          ) : (
                            <div>
                              <span className="text-sm text-green-500">✓ Available</span>
                            </div>
                          )}
                        </div>

                        {hasSpec && (
                          <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-zinc-800 pt-3 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-green-500">Boosted:</span>
                              <span className="text-zinc-400">
                                {glad.specialization.boosts.join(', ')} (+{Math.round(glad.specialization.bonus_pct * 100)}%)
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-red-500">Penalized:</span>
                              <span className="text-zinc-400">
                                {glad.specialization.penalties.join(', ')} (-{Math.round(glad.specialization.bonus_pct * 100)}%)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Mentor Assignment Section */}
          <section className="border border-zinc-800 bg-zinc-900/60 p-4 rounded-sm sm:p-6">
            <h2 className="mb-5 flex items-center gap-2 border-b border-zinc-800 pb-4 text-lg font-cinzel font-semibold uppercase tracking-wide text-amber-300">
              <UserCog className="w-5 h-5" />
              Personal Mentoring
            </h2>
            
            {sanctumData?.doctores?.length === 0 ? (
              <Empty label="Add a doctore first to begin mentoring"><UserCog className="mx-auto mb-3 text-zinc-700" size={38} /></Empty>
            ) : (
              <div>
                <p className="text-sm text-zinc-400 mb-4">
                  Assign a doctore to personally mentor a gladiator in one skill. Each doctore can mentor ONE gladiator at a time, providing ongoing training bonuses.
                </p>
                
                <div className="grid gap-3">
                  {activeGladiators.map((glad) => {
                    const mentor = sanctumData?.doctores?.find(d => d.assigned_to?.glad_id === glad.id);
                    
                    return (
                      <div
                        key={`mentor-${glad.id}`}
                        data-testid={`sanctum-mentor-${glad.id}`}
                        className="cursor-pointer border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm transition-colors hover:border-sky-700/50"
                        onClick={() => {
                          setSelectedGladiator(glad);
                          setShowAssignMentor(true);
                        }}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="font-semibold text-amber-100">{glad.name}</div>
                              <div className="text-xs text-zinc-500">
                                {glad.cls || glad.class} • Rating {glad.rating}
                              </div>
                            </div>
                          </div>
                          
                          {mentor ? (
                            <div className="flex items-center gap-2 text-sm">
                              <UserCog className="w-4 h-4 text-blue-500" />
                              <span className="text-zinc-400">
                                Mentored by {mentor.name} ({mentor.assigned_to.skill})
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-blue-500">Assign Mentor</span>
                              <UserCog className="w-4 h-4 text-blue-500" />
                            </div>
                          )}
                        </div>

                        {mentor && (
                          <div className="mt-3 border-t border-zinc-800 pt-3 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-500">Bonus:</span>
                                <span className="text-gray-400">
                                  +{Math.round(mentor.mentor_pct * 100)}% to {mentor.assigned_to.skill}
                                </span>
                              </div>
                              <button
                                data-testid={`sanctum-remove-mentor-${mentor.glad_id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Remove this mentor assignment?')) {
                                    try {
                                      await api.delete(`/sanctum/doctores/${mentor.glad_id}/assign`);
                                      await fetchData();
                                    } catch (err) {
                                      setError(err.response?.data?.detail || 'Failed to remove mentor');
                                    }
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Info Footer */}
          <aside data-testid="sanctum-guide" className="border border-zinc-800 bg-zinc-950/50 p-4 rounded-sm sm:p-6">
            <div className="flex items-start gap-3 text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1 text-zinc-400">
                <p>• Retired gladiators with 50+ battles can become doctores</p>
                <p>• Doctore tiers: Bronze (50), Silver (100), Gold (150), Elite (200), Legendary (300+ battles)</p>
                <p>• Each active gladiator can only specialize ONCE (permanent decision)</p>
                <p>• Sanctum starts with 1 slot, upgrade to unlock up to 5 doctore slots</p>
                <p>• Upgrade costs: 200g → 2 slots, 300g → 3 slots, 400g → 4 slots, 500g → 5 slots</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
      </div>

      {/* Modals */}
      {showAddDoctore && (
        <AddDoctoreModal
          sanctumData={sanctumData}
          onClose={() => setShowAddDoctore(false)}
          onSuccess={fetchData}
        />
      )}

      {selectedGladiator && !showAssignMentor && (
        <SpecializeModal
          gladiator={selectedGladiator}
          doctores={sanctumData?.doctores || []}
          onClose={() => setSelectedGladiator(null)}
          onSuccess={fetchData}
        />
      )}

      {selectedGladiator && showAssignMentor && (
        <AssignMentorModal
          gladiator={selectedGladiator}
          doctores={sanctumData?.doctores || []}
          onClose={() => {
            setSelectedGladiator(null);
            setShowAssignMentor(false);
          }}
          onSuccess={fetchData}
        />
      )}

      {showUpgradeModal && (
        <UpgradeSanctumModal
          currentSlots={sanctumData?.sanctum_slots || 1}
          cost={getUpgradeCost()}
          onClose={() => setShowUpgradeModal(false)}
          onSuccess={fetchData}
        />
      )}
    </main>
  );
};

export default DoctoreSanctumPage;
